<template>
  <Drawer
    :open="open"
    :nested="nested"
    :dismissible="dismissible"
    @update:open="handleDrawerOpenClose"
  >
    <DrawerTrigger v-if="$slots.trigger" :disabled="disabled" asChild>
      <slot name="trigger" />
    </DrawerTrigger>
    <DrawerContent
      :class="cn(contentVariants({ variant }), props.class)"
      @openAutoFocus="handlePreventFocus"
    >
      <DrawerHeader :class="{ '!p-0': headerIsEmpty }">
        <div v-if="$slots['header-start']">
          <slot name="header-start" />
        </div>
        <div v-if="$slots['feature-icon']" class="pb-2">
          <slot name="feature-icon" />
        </div>
        <DrawerTitle
          :class="
            cn(titleVariants({ variant: headerVariant || variant }), {
              hidden: !title,
            })
          "
        >
          {{ title }}
        </DrawerTitle>
        <DrawerDescription
          :class="
            cn(descriptionVariants({ variant: headerVariant || variant }), {
              hidden: !description,
            })
          "
        >
          {{ description }}
        </DrawerDescription>

        <div v-if="$slots['header-end']">
          <slot name="header-end" />
        </div>
      </DrawerHeader>
      <slot name="content" />
      <DrawerFooter v-if="$slots.footer" id="footer">
        <slot name="footer" />
      </DrawerFooter>
      <DrawerClose
        ref="close"
        v-if="withClose"
        :class="closeVariants({ variant: closeVariant || variant })"
      >
        <XClose
          class="w-6 h-6 fg-quinary hover:fg-quinary_hover transition-colors"
        />
      </DrawerClose>
    </DrawerContent>
  </Drawer>
</template>

<script setup>
import { computed, ref, useSlots } from "vue";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/Shadcn/Components/ui/drawer";
import { cn } from "@/Utils/cn";
import {
  contentVariants,
  titleVariants,
  descriptionVariants,
  closeVariants,
} from ".";
import { XClose } from "@/Icons";

const emit = defineEmits(["open", "close"]);

const slotKeys = Object.keys(useSlots());

const props = defineProps({
  variant: { type: null, required: false },
  headerVariant: { type: null, required: false },
  closeVariant: { type: null, required: false },
  open: { type: Boolean, default: undefined },
  title: { type: String, required: false },
  description: { type: String, required: false },
  class: { type: null, required: false },
  nested: { type: Boolean, required: false },
  disabled: { type: Boolean, default: false },
  dismissible: { type: Boolean, default: true },
  withClose: { type: Boolean, default: true },
});

const headerSlots = ["header-start", "feature-icon", "header-end"];
const headerIsEmpty = computed(
  () =>
    !props.title &&
    !props.description &&
    !headerSlots.some((slot) => slotKeys.includes(slot))
);

function handleDrawerOpenClose(isDrawerOpening) {
  if (isDrawerOpening) {
    emit("open");
    return;
  }

  emit("close");
}

// https://github.com/shadcn-ui/ui/issues/2831
// https://github.com/shadcn-ui/ui/issues/2849
// https://github.com/emilkowalski/vaul/issues/294
function handlePreventFocus(evt) {
  evt.preventDefault();
}

const close = ref(null);

function closeDrawer() {
  close.value.$el.click();
}

defineExpose({
  close: closeDrawer,
});
</script>
